<template>
    <q-dialog
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <q-card style="min-width: 520px; max-width: 95vw;">
            <q-card-section>
                <h5 class="q-my-none">Assign to Paua Client</h5>
                <div class="q-mt-md">
                    <div><strong>Te Kohinga Person:</strong> {{ personName || '-' }}</div>
                </div>
                <q-banner v-if="alreadyLinked" rounded class="bg-orange-1 text-orange-9 q-mt-sm">
                    This Te Kohinga Person is already linked to a Paua client. Selecting another will overwrite the link.
                </q-banner>
            </q-card-section>

            <q-separator />

            <q-card-section>
                <ClientSelect v-model="selectedClient" />
            </q-card-section>

            <q-separator />

            <q-card-section>
                <q-banner v-if="loadingResponses" rounded class="bg-grey-2 text-grey-9">
                    <q-spinner size="sm" class="q-mr-sm" />Checking existing form responses…
                </q-banner>
                <q-banner v-else-if="responsesError" rounded class="bg-red-1 text-warning">
                    {{ responsesError }}
                </q-banner>
                <div v-else class="text-body2">
                    <span v-if="responseIds.length === 0">
                        No existing form responses are linked to this Te Kohinga Person.
                    </span>
                    <span v-else>
                        {{ responseIds.length }} form
                        response{{ responseIds.length === 1 ? '' : 's' }} will be moved to the selected
                        Paua client.
                    </span>
                </div>
            </q-card-section>

            <q-separator />

            <q-card-actions align="right">
                <q-btn flat rounded no-caps color="negative" label="Cancel" v-close-popup />
                <q-btn
                    flat rounded no-caps outline
                    color="primary"
                    label="Assign"
                    :disable="!selectedClient || loadingResponses"
                    :loading="saving"
                    @click="onConfirm"
                />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import ClientSelect from "@/components/ClientComponents/ClientSelectComponent.vue";
import { tkPersonName, tkPersonSubject } from "@/services/util/formSubject";

export default {
    name: "TkPersonLinkPauaDialog",
    components: { ClientSelect },
    emits: ["update:modelValue", "linked"],
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        person: {
            type: [Object, null],
            default: null,
        },
    },
    data() {
        return {
            selectedClient: null,
            responseIds: [],
            loadingResponses: false,
            responsesError: null,
            saving: false,
        };
    },
    computed: {
        personName() {
            return tkPersonName(this.person);
        },
        alreadyLinked() {
            return this.person?.linked_paua_client_id != null;
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.selectedClient = null;
                this.responseIds = [];
                this.responsesError = null;
                this.loadResponseIds();
            }
        },
    },
    methods: {
        async loadResponseIds() {
            if (!this.person?.id) {
                this.responseIds = [];
                return;
            }
            this.loadingResponses = true;
            this.responsesError = null;
            try {
                const subject = tkPersonSubject(this.person);
                const formTypesRes = await api.forms.getDynamicFormTypes();
                const formTypes = formTypesRes?.data?.data || [];

                const historyRequests = formTypes.map(async (formType) => {
                    const res = await api.forms.getFormHistory(formType.id, subject);
                    const history = res?.data?.data || [];
                    return history.map((item) => Number(item.id)).filter((id) => Number.isFinite(id));
                });

                const ids = (await Promise.all(historyRequests)).flat();
                this.responseIds = Array.from(new Set(ids));
            } catch (e) {
                this.responsesError = "Failed to load existing form responses.";
                this.responseIds = [];
            } finally {
                this.loadingResponses = false;
            }
        },
        async onConfirm() {
            if (!this.person?.id || !this.selectedClient?.client_id) return;
            this.saving = true;
            try {
                await api.tkPeople.linkPaua(this.person.id, {
                    paua_client_id: this.selectedClient.client_id,
                });
                if (this.responseIds.length) {
                    await api.forms.assignToClient(this.selectedClient, this.responseIds);
                }
                notify.primary("Te Kohinga Person mapped to Paua client.", "top");
                this.$emit("linked", { client: this.selectedClient, responseIds: this.responseIds });
                this.$emit("update:modelValue", false);
            } catch (e) {
                notify.error("Failed to map Te Kohinga Person. Please try again.", "top");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
